import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

// Add CSS for animations and hover effects
const injectStyles = () => {
  const styleId = 'register-styles';
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    [data-register-input]:focus, [data-register-select]:focus {
      border-color: #667eea !important;
      box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.15) !important;
      outline: none;
    }
    
    [data-register-button]:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4) !important;
    }
    
    [data-register-button]:active:not(:disabled) {
      transform: translateY(0) scale(0.98);
    }
    
    [data-register-link]:hover {
      text-decoration: underline;
    }
    
    /* Mobile responsive layout for Register form */
    @media (max-width: 768px) {
      [data-register-row] {
        flex-direction: column !important;
        gap: 12px !important;
      }
      [data-register-card] {
        padding: 24px 16px !important;
        margin: 0 8px !important;
        max-height: 90vh !important;
        overflow-y: auto !important;
      }
      [data-register-column] {
        width: 100% !important;
        min-width: 0 !important;
      }
    }
  `;
  document.head.appendChild(style);
};

// Fallback language list to ensure dropdowns are populated even if API fails/hangs
const fallbackLanguages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hi', name: 'Hindi' },
  { code: 'bn', name: 'Bengali' },
];

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    prolific_id: '',
    password: '',
    confirmPassword: '',
    age: '',
    native_language: '',
    language2: 'none',
    language3: 'none'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState(fallbackLanguages);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    injectStyles();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch supported languages on component mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/languages`, { timeout: 3000 });
        const data = response && response.data ? response.data : [];
        const raw = Array.isArray(data) ? data : (Array.isArray(data.languages) ? data.languages : []);
        const normalized = raw.map(item => (typeof item === 'string' ? { code: item, name: item } : item))
          .filter(l => l && l.code && l.name);
        if (normalized.length > 0) setLanguages(normalized);
      } catch (error) {
        console.error('Failed to fetch languages:', error);
        // Keep fallbackLanguages already set
      }
    };
    fetchLanguages();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.prolific_id.trim()) {
      newErrors.prolific_id = 'Prolific ID is required';
    }

    // Full name validation
    if (!formData.full_name || formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Full name must be at least 2 characters long';
    }

    // Age validation
    const age = parseInt(formData.age);
    if (!formData.age) {
      newErrors.age = 'Age is required';
    } else if (age < 18 || age > 100) {
      newErrors.age = 'Age must be between 18 and 100';
    }

    // Language validation
    if (!formData.native_language) {
      newErrors.native_language = 'Native language is required';
    }

    // Check if selected languages are different (ignoring 'none')
    const selectedLanguages = [formData.native_language, formData.language2, formData.language3].filter(lang => lang && lang !== 'none');
    const uniqueLanguages = new Set(selectedLanguages);
    if (uniqueLanguages.size !== selectedLanguages.length) {
      newErrors.languages = 'Languages must be different (excluding None)';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/[A-Za-z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one letter';
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const payload = {
      email: formData.email,
      full_name: formData.full_name.trim(),
      password: formData.password,
      age: parseInt(formData.age),
      native_language: formData.native_language,
      language2: formData.language2 || 'none',
      language3: formData.language3 || 'none',
      prolific_id: formData.prolific_id.trim()
    };
    const result = await register(payload);

    if (result.success) {
      navigate('/login');
    } else {
      // Handle validation errors from backend
      if (result.error && typeof result.error === 'object') {
        const errorMessages = [];
        if (Array.isArray(result.error)) {
          result.error.forEach(err => {
            if (err.msg) errorMessages.push(err.msg);
          });
        }
        setErrors({ submit: errorMessages.join(', ') || 'Registration failed' });
      } else {
        setErrors({ submit: result.error || 'Registration failed' });
      }
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div style={{
      ...styles.container,
      padding: isMobile ? '10px' : '20px'
    }}>
      <div 
        data-register-card
        style={{
          ...styles.card,
          borderRadius: isMobile ? '16px' : '24px',
          padding: isMobile ? '20px 16px' : '48px',
          maxWidth: isMobile ? '100%' : '760px',
          maxHeight: isMobile ? '90vh' : 'none',
          overflowY: isMobile ? 'auto' : 'visible',
          margin: isMobile ? '0 8px' : '0',
          boxSizing: 'border-box'
        }}>
        <div style={styles.iconContainer}>
          <div style={{
            ...styles.icon,
            width: isMobile ? 48 : 64,
            height: isMobile ? 48 : 64,
            fontSize: isMobile ? 24 : 32
          }}>✨</div>
        </div>
        <h2 style={{
          ...styles.title,
          fontSize: isMobile ? '22px' : '32px'
        }}>Create Account</h2>
        <p style={{
          ...styles.subtitle,
          marginBottom: isMobile ? '20px' : '32px',
          fontSize: isMobile ? '13px' : '15px'
        }}>Join our multilingual chat community</p>
        
        {errors.submit && (
          <div style={styles.errorMessage}>
            {errors.submit}
          </div>
        )}
        
        {errors.languages && (
          <div style={styles.errorMessage}>
            {errors.languages}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{
          ...styles.form,
          gap: isMobile ? '12px' : '20px'
        }}>
          {/* Personal Information Row */}
          <div 
            data-register-row
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '20px',
              marginBottom: '20px'
            }}>
            <div 
              data-register-column
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                minWidth: 0
              }}>
              <div style={styles.inputGroup}>
                <label style={{...styles.label, fontSize: isMobile ? '13px' : '14px'}}>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.email ? styles.inputError : {}),
                    padding: isMobile ? '12px 14px' : '14px 18px',
                    fontSize: isMobile ? '14px' : '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter your email"
                  data-register-input
                />
                {errors.email && <span style={styles.errorText}>{errors.email}</span>}
              </div>

              <div style={styles.inputGroup}>
                <label style={{...styles.label, fontSize: isMobile ? '13px' : '14px'}}>Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.full_name ? styles.inputError : {}),
                    padding: isMobile ? '12px 14px' : '14px 18px',
                    fontSize: isMobile ? '14px' : '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter your full name"
                  data-register-input
                />
                {errors.full_name && <span style={styles.errorText}>{errors.full_name}</span>}
              </div>

              <div style={styles.inputGroup}>
                <label style={{...styles.label, fontSize: isMobile ? '13px' : '14px'}}>Prolific participant ID</label>
                <input
                  type="text"
                  name="prolific_id"
                  value={formData.prolific_id}
                  onChange={handleChange}
                  autoComplete="off"
                  style={{
                    ...styles.input,
                    ...(errors.prolific_id ? styles.inputError : {}),
                    padding: isMobile ? '12px 14px' : '14px 18px',
                    fontSize: isMobile ? '14px' : '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter your Prolific participant ID"
                  data-register-input
                />
                {errors.prolific_id && <span style={styles.errorText}>{errors.prolific_id}</span>}
                <span style={{ fontSize: 12, color: '#6b7280', marginTop: 4, display: 'block' }}>
                  Required. It must match your Prolific study ID and cannot be used twice.
                </span>
              </div>

              <div style={styles.inputGroup}>
                <label style={{...styles.label, fontSize: isMobile ? '13px' : '14px'}}>Age</label>
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  min="18"
                  max="100"
                  style={{
                    ...styles.input,
                    ...(errors.age ? styles.inputError : {}),
                    padding: isMobile ? '12px 14px' : '14px 18px',
                    fontSize: isMobile ? '14px' : '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter your age"
                  data-register-input
                />
                {errors.age && <span style={styles.errorText}>{errors.age}</span>}
              </div>
            </div>

            <div 
              data-register-column
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                minWidth: 0
              }}>
              <div style={styles.inputGroup}>
                <label style={{...styles.label, fontSize: isMobile ? '13px' : '14px'}}>Native Language *</label>
                <select
                  name="native_language"
                  value={formData.native_language}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.native_language ? styles.inputError : {}),
                    padding: isMobile ? '12px 14px' : '14px 18px',
                    fontSize: isMobile ? '14px' : '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  data-register-select
                >
                  <option value="">Select your native language</option>
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {errors.native_language && <span style={styles.errorText}>{errors.native_language}</span>}
              </div>

              <div style={styles.inputGroup}>
                <label style={{...styles.label, fontSize: isMobile ? '13px' : '14px'}}>Second Language (optional)</label>
                <select
                  name="language2"
                  value={formData.language2}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.language2 ? styles.inputError : {}),
                    padding: isMobile ? '12px 14px' : '14px 18px',
                    fontSize: isMobile ? '14px' : '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  data-register-select
                >
                  <option value="none">None</option>
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {errors.language2 && <span style={styles.errorText}>{errors.language2}</span>}
              </div>

              <div style={styles.inputGroup}>
                <label style={{...styles.label, fontSize: isMobile ? '13px' : '14px'}}>Third Language (optional)</label>
                <select
                  name="language3"
                  value={formData.language3}
                  onChange={handleChange}
                  style={{
                    ...styles.input,
                    ...(errors.language3 ? styles.inputError : {}),
                    padding: isMobile ? '12px 14px' : '14px 18px',
                    fontSize: isMobile ? '14px' : '16px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  data-register-select
                >
                  <option value="none">None</option>
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {errors.language3 && <span style={styles.errorText}>{errors.language3}</span>}
              </div>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={{...styles.label, fontSize: isMobile ? '13px' : '14px'}}>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              style={{
                ...styles.input,
                ...(errors.password ? styles.inputError : {}),
                padding: isMobile ? '12px 14px' : '14px 18px',
                fontSize: isMobile ? '14px' : '16px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              placeholder="Create a password"
              data-register-input
            />
            {errors.password && <span style={styles.errorText}>{errors.password}</span>}
          </div>

          <div style={styles.inputGroup}>
            <label style={{...styles.label, fontSize: isMobile ? '13px' : '14px'}}>Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              style={{
                ...styles.input,
                ...(errors.confirmPassword ? styles.inputError : {}),
                padding: isMobile ? '12px 14px' : '14px 18px',
                fontSize: isMobile ? '14px' : '16px',
                width: '100%',
                boxSizing: 'border-box'
              }}
              placeholder="Confirm your password"
              data-register-input
            />
            {errors.confirmPassword && <span style={styles.errorText}>{errors.confirmPassword}</span>}
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
              padding: isMobile ? '12px 20px' : '14px 28px',
              fontSize: isMobile ? '15px' : '17px'
            }}
            data-register-button
          >
            {loading ? '⏳ Creating Account...' : '🎉 Create Account'}
          </button>
        </form>

        <p style={{
          ...styles.footer,
          marginTop: isMobile ? '16px' : '24px',
          fontSize: isMobile ? '13px' : '14px'
        }}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link} data-register-link>
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '24px',
    padding: '48px',
    boxShadow: '0 25px 50px rgba(102, 126, 234, 0.25)',
    width: '100%',
    maxWidth: '760px',
    animation: 'fadeInUp 0.6s ease-out',
    border: '2px solid transparent',
    backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundOrigin: 'border-box',
    backgroundClip: 'padding-box, border-box',
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 20,
  },
  icon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 32,
    boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
  },
  title: {
    fontSize: '32px',
    fontWeight: 700,
    textAlign: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    marginBottom: '8px',
  },
  subtitle: {
    textAlign: 'center',
    color: '#6b7280',
    marginBottom: '32px',
    fontSize: '15px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: '20px',
    marginBottom: '20px',
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  input: {
    padding: '14px 18px',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    backgroundColor: '#f9fafb',
  },
  inputError: {
    borderColor: '#ef4444',
    boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
  },
  errorText: {
    fontSize: '12px',
    color: '#ef4444',
    marginTop: '4px',
  },
  errorMessage: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  button: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '14px 28px',
    border: 'none',
    borderRadius: '12px',
    fontSize: '17px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '10px',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
  },
  buttonDisabled: {
    background: '#9ca3af',
    cursor: 'not-allowed',
    boxShadow: '0 2px 8px rgba(156, 163, 175, 0.2)',
  },
  footer: {
    textAlign: 'center',
    marginTop: '24px',
    color: '#6b7280',
  },
  link: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    textDecoration: 'none',
    fontWeight: 600,
  },
};

export default Register;
